import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  HStack,
  Button,
  Select,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  Badge,
  Divider,
  Flex,
  Icon,
  Wrap,
  WrapItem,
  useBreakpointValue,
  Input,
} from '@chakra-ui/react'
import { ExternalLinkIcon, ViewIcon, CopyIcon } from '@chakra-ui/icons'
import AssemblyViewer from './AssemblyViewer'

interface CompilerExplorerProps {
  sourceCode: string
  compilerOptions?: string
  onEnterAssemblyView?: (assemblyOutput: string) => void
}

interface CompilerExplorerResponse {
  code: number
  stdout: Array<{
    text: string
    tag?: {
      line: number
      text: string
    }
  }>
  stderr: Array<{
    text: string
    tag?: {
      line: number
      text: string
    }
  }>
  asm: Array<{
    text: string
    source: {
      file: string | null
      line: number
    } | null
  }>
  tools: any[]
  okToCache: boolean
  optOutput?: {
    displayString: string
    Pass: string
    Name: string
    DebugLoc: {
      File: string
      Line: number
      Column: number
    }
    Function: string
    Args: any[]
  }
}

// Updated compiler IDs for official Godbolt API
// These IDs need to match what's available on godbolt.org
const COMPILER_OPTIONS = [
  { value: 'gcc1220', label: 'GCC 12.2.0' },
  { value: 'gcc1320', label: 'GCC 13.2.0' },
  { value: 'gcc1410', label: 'GCC 14.1.0' },
  { value: 'clang1600', label: 'Clang 16.0.0' },
  { value: 'clang1700', label: 'Clang 17.0.0' },
]

const OUTPUT_FORMATS = [
  { value: 'asm', label: 'Assembly' },
  { value: 'llvm-ir', label: 'LLVM IR' },
]

export default function CompilerExplorer({ sourceCode, compilerOptions = '-O2', onEnterAssemblyView }: CompilerExplorerProps) {
  // NOTE: Currently using official Godbolt API endpoints (https://godbolt.org/api/...)
  // TODO: Switch back to custom server when available
  
  const [selectedCompiler, setSelectedCompiler] = useState('gcc1320') // Changed to gcc1320
  const [selectedFormat, setSelectedFormat] = useState('asm')
  const [editableCompilerOptions, setEditableCompilerOptions] = useState(compilerOptions)
  const [isLoading, setIsLoading] = useState(false)
  const [output, setOutput] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [isOpeningGodbolt, setIsOpeningGodbolt] = useState(false)
  const toast = useToast()
  const outputRef = useRef<HTMLDivElement>(null)

  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const bgColor = useColorModeValue('white', 'gray.800')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  
  // Responsive layout - more aggressive breakpoints for narrow panels
  const isCompact = useBreakpointValue({ base: true, lg: false })
  const headerDirection = useBreakpointValue({ base: 'column', xl: 'row' }) as 'column' | 'row'
  const controlsDirection = useBreakpointValue({ base: 'column', lg: 'row' }) as 'column' | 'row'

  // Update editable compiler options when prop changes
  useEffect(() => {
    setEditableCompilerOptions(compilerOptions)
  }, [compilerOptions])

  // Remove OpenCilk flags for Godbolt (they don't support OpenCilk)
  const getGodboltCompilerOptions = (options: string) => {
    return options.replace(/-fopencilk\s*/g, '').trim()
  }



  const fetchAssembly = async () => {
    if (!sourceCode.trim()) {
      toast({
        title: 'No Source Code',
        description: 'Please write some code first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsLoading(true)
    setError('')
    setOutput('')

    try {
      // Use the official Godbolt API endpoint
      const endpoint = `https://godbolt.org/api/compiler/${selectedCompiler}/compile`
      // Adjust options for LLVM IR and remove OpenCilk flags
      let opts = getGodboltCompilerOptions(editableCompilerOptions)
      if (selectedFormat === 'llvm-ir') {
        opts = opts + ' -S -emit-llvm'
      }
      
      const requestBody = {
        source: sourceCode,
        options: {
          userArguments: opts,
          compilerOptions: {
            skipAsm: false,
            executorRequest: false,
            overrides: []
          },
          filters: {
            binary: false,
            binaryObject: false,
            commentOnly: false,
            demangle: true,
            directives: true,
            execute: false,
            intel: false,
            labels: true,
            libraryCode: false,
            trim: false,
            debugCalls: false
          },
          tools: [],
          libraries: [],
          executeParameters: {
            args: [],
            stdin: "",
            runtimeTools: []
          }
        },
        lang: "c++",
        allowStoreCodeDebug: true
      }



      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
      })

      let data: CompilerExplorerResponse | null = null
      let text = await response.text()
      
      // Handle error responses
      if (!response.ok) {
        setError(`API Error ${response.status}: ${text || response.statusText}`)
        return
      }
      
      try {
        data = JSON.parse(text)
      } catch (err) {
        // Not JSON, show as error
        setError('Compiler Explorer error: ' + text.substring(0, 500))
        return
      }

      if (!data) {
        setError('Compiler Explorer returned no data.')
        return
      }
      if (data.code !== 0) {
        setError('Compilation failed. Please check your code.')
        return
      }
      if (data.asm && data.asm.length > 0) {
        // Pass the raw JSON response to AssemblyViewer for proper parsing
        const assemblyOutput = JSON.stringify(data)
        setOutput(assemblyOutput)
      } else {
        setError('No output generated.')
      }
    } catch (err) {
      console.error('Compiler Explorer API error:', err)
      setError('Failed to fetch assembly. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const openInCompilerExplorer = async () => {
    if (!sourceCode.trim()) return
    setIsOpeningGodbolt(true)
    setError("")
    try {
      const payload = {
        sessions: [
          {
            language: "c++",
            source: sourceCode,
            compilers: [
              {
                id: selectedCompiler,
                options: getGodboltCompilerOptions(editableCompilerOptions),
              },
            ],
          },
        ],
      }
      // Use official Godbolt shortener API
      const response = await fetch('https://godbolt.org/api/shortener', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) throw new Error('Failed to create Godbolt short link')
      const data = await response.json()
      if (data && data.url) {
        window.open(`https://godbolt.org${data.url}`, '_blank')
      } else {
        setError('Failed to get Godbolt short link')
      }
    } catch (err) {
      setError('Failed to open in Godbolt: ' + (err instanceof Error ? err.message : 'Unknown error'))
    } finally {
      setIsOpeningGodbolt(false)
    }
  }

  return (
    <Box border="1px" borderColor={borderColor} borderRadius="md" bg={bgColor}>
      <Box p={4} borderBottom="1px" borderColor={borderColor}>
        <VStack spacing={3} align="stretch">
          <Flex direction={headerDirection} justify="space-between" align={headerDirection === 'column' ? 'stretch' : 'center'} gap={3}>
            <HStack spacing={2}>
              <Icon as={ViewIcon} boxSize={4} color="blue.500" />
              <Text fontSize={isCompact ? "md" : "lg"} fontWeight="semibold" color={textColor}>
                Compiler Explorer
              </Text>
              <Badge colorScheme="blue" variant="subtle">
                Live
              </Badge>
            </HStack>
            <Button
              size="sm"
              leftIcon={<ExternalLinkIcon />}
              onClick={openInCompilerExplorer}
              isDisabled={!sourceCode.trim()}
              colorScheme="blue"
              variant="outline"
              isLoading={isOpeningGodbolt}
              width={headerDirection === 'column' ? 'full' : 'auto'}
              flexShrink={0}
            >
              Open in Godbolt
            </Button>
          </Flex>

          <Flex direction={controlsDirection} wrap="wrap" gap={4} align={controlsDirection === 'column' ? 'stretch' : 'start'}>
            <Box flex={controlsDirection === 'column' ? 'none' : 1} minW={controlsDirection === 'row' ? '140px' : 'auto'}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Compiler
              </Text>
              <Select
                size="sm"
                value={selectedCompiler}
                onChange={(e) => setSelectedCompiler(e.target.value)}
                width="100%"
              >
                {COMPILER_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={controlsDirection === 'column' ? 'none' : 1} minW={controlsDirection === 'row' ? '120px' : 'auto'}>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Output Format
              </Text>
              <Select
                size="sm"
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                width="100%"
              >
                {OUTPUT_FORMATS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Box>

            <Box flex={controlsDirection === 'column' ? 'none' : 2} minW="150px">
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Compiler Options
              </Text>
              <Input
                size="sm"
                value={editableCompilerOptions}
                onChange={(e) => setEditableCompilerOptions(e.target.value)}
                placeholder="-O2"
                fontFamily="mono"
                fontSize="xs"
              />
            </Box>
          </Flex>

                      <HStack spacing={2}>
              <Button
                size="sm"
                onClick={fetchAssembly}
                isLoading={isLoading}
                loadingText="Generating..."
                colorScheme="blue"
              >
                Generate Assembly
              </Button>
              {output && onEnterAssemblyView && (
                <Button
                  size="sm"
                  onClick={() => onEnterAssemblyView(output)}
                  colorScheme="orange"
                  variant="outline"
                >
                  Assembly View
                </Button>
              )}

            </HStack>
        </VStack>
      </Box>

      <Box p={4}>

        
        {isLoading && (
          <Flex justify="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

                            {output && !isLoading && (
            <AssemblyViewer 
              assemblyText={output}
              sourceCode={sourceCode}
              onCopy={(text) => {
                toast({
                  title: 'Copied!',
                  description: 'Assembly code copied to clipboard.',
                  status: 'success',
                  duration: 2000,
                  isClosable: true,
                })
              }}
            />
          )}

        {!output && !isLoading && !error && (
          <Box textAlign="center" py={8} color="gray.500">
            <Text>Click "Generate Assembly" to see the compiled output</Text>

          </Box>
        )}
      </Box>
    </Box>
  )
} 